import { unreachable } from "@mdotm/utils/runtime";
import type {
	ConceptDefinitionDTODataTypeEnum,
	DictionaryData,
	RichAcl,
	SmartWorkspaceStatisticsDTOItemStatusEnum,
} from "@eu.mdotm.api/smart-workspaces";
import type { BreakdownValueDTO, TimeSeriesValueDTO } from "./spec-gaps";

export type WorkspaceEntityParameter = {
	type: "REFERENCE" | "MARKET_VIEW" | "UNIVERSE" | "INVESTMENT" | "BENCHMARK";
	uuid: string;
	name: string;
};

/**
 * A type carried as a value.
 *
 * `type` is never read at runtime — the object is empty. It exists so that the *shape* of a
 * `dataType`'s value and the runtime facts about that same `dataType` can live in one table keyed on
 * one enum, instead of in a value table plus two hand-maintained conditional-type chains that nothing
 * forces to agree. See {@link workspaceDataTypeValues}.
 */
type Shape<T> = { readonly type: T };
function shape<T>(): Shape<T> {
	return {} as Shape<T>;
}

/**
 * What a concept's value *is*, per `dataType`: on the options endpoint, in a data-response cell, and
 * how the cell wraps it.
 *
 * One row per `dataType`, closed with `satisfies Record<ConceptDefinitionDTODataTypeEnum, …>`, so a new
 * data type cannot be added without answering all three questions. It replaces three parallel restatements
 * of the same enum — two nested conditional-type chains (`OptionsProvider`, `DataProvider`) and a `switch`
 * over the cell wrapping — which agreed only by inspection. They differ in exactly two rows, and the table
 * is where you can now see that:
 *
 * - `CUSTOM_ATTRIBUTE_TAG` publishes tag *definitions* as options (a name and its colour) but stores the
 *   selected tag *names* in a cell;
 * - `TIME_SERIES` has no options at all — `/concepts/{conceptId}/options` is meaningless for it, so its
 *   option shape is `never` and {@link workspaceOptionsProviderCast} throws rather than casting.
 *
 * `encoding` is how the cell wraps the value: `nested` means `dictionary[uuid].value` is a `{value: …}`
 * envelope to be unwrapped once, `plain` means it is the value itself. Every custom attribute is nested and
 * nothing else is, but the fact belongs per row rather than in an `isCustomAttribute` check, because it
 * describes the wire and not the concept's provenance.
 */
const workspaceDataTypeValues = {
	BOOLEAN: { option: shape<boolean>(), cell: shape<boolean>(), encoding: "plain" },
	STRING: { option: shape<string>(), cell: shape<string>(), encoding: "plain" },
	NUMERIC: { option: shape<number>(), cell: shape<number>(), encoding: "plain" },
	DATE: { option: shape<string>(), cell: shape<string>(), encoding: "plain" },
	TIME: { option: shape<string>(), cell: shape<string>(), encoding: "plain" },
	DATETIME: { option: shape<string>(), cell: shape<string>(), encoding: "plain" },
	ENUM: { option: shape<string>(), cell: shape<string>(), encoding: "plain" },
	STATUS: {
		option: shape<SmartWorkspaceStatisticsDTOItemStatusEnum>(),
		cell: shape<SmartWorkspaceStatisticsDTOItemStatusEnum>(),
		encoding: "plain",
	},
	SHARING_INFO: { option: shape<RichAcl>(), cell: shape<RichAcl>(), encoding: "plain" },
	TAGS: { option: shape<string>(), cell: shape<string>(), encoding: "plain" },
	SPHERE_ENTITY: {
		option: shape<WorkspaceEntityParameter>(),
		cell: shape<WorkspaceEntityParameter>(),
		encoding: "plain",
	},
	CUSTOM_ATTRIBUTE_TAG: {
		option: shape<{ value: string; color?: string }>(),
		cell: shape<string[]>(),
		encoding: "nested",
	},
	CUSTOM_ATTRIBUTE_DATE: { option: shape<string>(), cell: shape<string>(), encoding: "nested" },
	CUSTOM_ATTRIBUTE_NUMBER: { option: shape<number>(), cell: shape<number>(), encoding: "nested" },
	CUSTOM_ATTRIBUTE_TEXT: { option: shape<string>(), cell: shape<string>(), encoding: "nested" },
	// An array rather than a scalar, but read the same way: the backend puts it straight in `value`, and an
	// absent series arrives as `[]` (no value for the window) rather than as a message.
	TIME_SERIES: { option: shape<never>(), cell: shape<TimeSeriesValueDTO>(), encoding: "plain" },
} satisfies Record<
	ConceptDefinitionDTODataTypeEnum,
	{ option: Shape<unknown>; cell: Shape<unknown>; encoding: "plain" | "nested" }
>;

// --- Namespace: WorkspaceDataCast ---

export declare namespace WorkspaceDataCast {
	/** Cast type for getConceptOptions items */
	type OptionsProvider<T extends ConceptDefinitionDTODataTypeEnum> =
		(typeof workspaceDataTypeValues)[T]["option"]["type"];

	/** Cast type for dictionary/row data values. `TIME_SERIES` is the one non-scalar entry. */
	type DataProvider<T extends ConceptDefinitionDTODataTypeEnum> = (typeof workspaceDataTypeValues)[T]["cell"]["type"];

	/**
	 * Cast type for the value of a concept requested *with* a `breakdown`.
	 *
	 * Deliberately keyed off the request (`breakdown != null` on the requesting parameterized concept)
	 * rather than off a new `dataType` value: a breakdown is a different *shape of the same metric*, and
	 * a new `dataType` would force every per-`dataType` answer in the area to grow a row it has nothing
	 * to say about.
	 */
	type Breakdown = BreakdownValueDTO;
}

/** @deprecated Use `WorkspaceDataCast.OptionsProvider<T>` instead */
export type WorkspaceOptionsProviderCast<T extends ConceptDefinitionDTODataTypeEnum> =
	WorkspaceDataCast.OptionsProvider<T>;

/** @deprecated Use `WorkspaceDataCast.DataProvider<T>` instead */
export type WorkspaceDataProviderCast<T extends ConceptDefinitionDTODataTypeEnum> = WorkspaceDataCast.DataProvider<T>;

/**
 * Reads an options-endpoint item as the shape its `dataType` publishes.
 *
 * Every `dataType` but one casts and nothing more — the endpoint hands back the value already in that
 * shape — so the whole of this used to be a sixteen-case `switch` whose branches were a literal copy of
 * each other. The single real answer is `TIME_SERIES`: it has no option list, `OptionsProvider<"TIME_SERIES">`
 * is `never`, and being called for it is a programming error rather than a value to cast.
 */
export function workspaceOptionsProviderCast<T extends ConceptDefinitionDTODataTypeEnum>(dataType: T) {
	return (value: Object): WorkspaceDataCast.OptionsProvider<T> => {
		if (dataType === "TIME_SERIES") {
			return unreachable();
		}
		return value as WorkspaceDataCast.OptionsProvider<T>;
	};
}

// --- Data Provider Extraction ---

export type DataProviderResult<T> = { value: T; message?: undefined } | { value?: undefined; message: string };

export type CustomAttributeConceptDataType = Extract<
	ConceptDefinitionDTODataTypeEnum,
	"CUSTOM_ATTRIBUTE_NUMBER" | "CUSTOM_ATTRIBUTE_DATE" | "CUSTOM_ATTRIBUTE_TEXT" | "CUSTOM_ATTRIBUTE_TAG"
>;

/**
 * Decodes a dictionary cell.
 *
 * Pass `{breakdown: true}` when the requesting parameterized concept carried a `breakdown`: the value
 * is then a {@link WorkspaceDataCast.Breakdown} (one entry per category) instead of a scalar,
 * regardless of the concept's `dataType`.
 */
export function extractDataProviderValue(
	raw: DictionaryData,
	dataType: ConceptDefinitionDTODataTypeEnum,
	opt: { breakdown: true },
): DataProviderResult<WorkspaceDataCast.Breakdown>;
export function extractDataProviderValue<T extends ConceptDefinitionDTODataTypeEnum>(
	raw: DictionaryData,
	dataType: T,
	opt?: { breakdown?: false },
): DataProviderResult<WorkspaceDataCast.DataProvider<T>>;
export function extractDataProviderValue<T extends ConceptDefinitionDTODataTypeEnum>(
	raw: DictionaryData,
	dataType: T,
	opt?: { breakdown?: boolean },
): DataProviderResult<WorkspaceDataCast.DataProvider<T>> | DataProviderResult<WorkspaceDataCast.Breakdown> {
	if (raw.message && !raw.value) {
		return { message: raw.message };
	}

	if (opt?.breakdown) {
		const wrapper = raw.value as WorkspaceDataCast.Breakdown | undefined;
		if (!wrapper?.entries) {
			return { message: raw.message ?? "" };
		}
		return { value: wrapper };
	}

	const values = workspaceDataTypeValues[dataType] ?? unreachable(dataType as never);
	if (values.encoding === "nested") {
		const inner = raw.value as { value: unknown } | undefined;
		if (!inner) {
			return { message: raw.message ?? "" };
		}
		return { value: inner.value as WorkspaceDataCast.DataProvider<T> };
	}

	return { value: raw.value as WorkspaceDataCast.DataProvider<T> };
}
